import React, { useState } from 'react';
import { isSaved, saveMed, removeMed } from '../api/meds';

function matchAny(reaction, terms) {
  const r = reaction.toLowerCase();
  return terms.some(t => r.includes(t));
}

// ClinicalTrials.gov dates come as "YYYY", "YYYY-MM", or "YYYY-MM-DD"
function parseTrialDate(dateStr) {
  if (!dateStr || dateStr === 'Unknown') return null;
  const parts = dateStr.split('-');
  const year = parseInt(parts[0], 10);
  if (isNaN(year)) return null;
  const month = parts[1] ? parseInt(parts[1], 10) - 1 : 0;
  const day = parts[2] ? parseInt(parts[2], 10) : 1;
  return new Date(year, month, day);
}

function getTrialDurationMonths(trial) {
  const start = parseTrialDate(trial.startDate);
  const end = parseTrialDate(trial.completionDate);
  if (!start || !end) return null;
  const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  return months > 0 ? months : null;
}

function getLongTermSafety(trials) {
  const withDuration = trials
    .filter(t => t.status === 'COMPLETED')
    .map(t => ({ ...t, durationMonths: getTrialDurationMonths(t) }))
    .filter(t => t.durationMonths !== null);

  const phase4Trials = trials.filter(t => t.phase === 'PHASE4');

  if (withDuration.length === 0) {
    return { hasDurationData: false, phase4Count: phase4Trials.length };
  }

  const longest = withDuration.reduce((max, t) => (t.durationMonths > max.durationMonths ? t : max), withDuration[0]);
  const yearsOrMoreCount = withDuration.filter(t => t.durationMonths >= 12).length;
  const phase4WithDuration = withDuration.filter(t => t.phase === 'PHASE4');

  return {
    hasDurationData: true,
    longestMonths: longest.durationMonths,
    yearsOrMoreCount,
    totalWithDuration: withDuration.length,
    phase4Count: phase4Trials.length,
    phase4LongestMonths: phase4WithDuration.length > 0
      ? Math.max(...phase4WithDuration.map(t => t.durationMonths))
      : null,
  };
}

function formatMonths(months) {
  if (months < 12) return `${months} month${months === 1 ? '' : 's'}`;
  const years = Math.round((months / 12) * 10) / 10;
  return `${years} year${years === 1 ? '' : 's'}`;
}

function LongTermSafety({ trials }) {
  const info = getLongTermSafety(trials);

  return (
    <div id="long-term" className="result-section">
      <h3>Long-Term Safety Data</h3>
      <p className="section-note">Based on the duration of completed trials on record — not a guarantee about effects from many years of continuous use.</p>

      {!info.hasDurationData ? (
        <p>
          {info.phase4Count > 0
            ? `Trial dates weren't available to calculate duration, but ${info.phase4Count} Phase 4 (post-market) trial${info.phase4Count > 1 ? 's are' : ' is'} on record — these monitor the drug in real-world use after approval.`
            : `Trial start and completion dates weren't available for this drug, so trial duration can't be calculated from this data.`}
        </p>
      ) : (
        <>
          <p>
            The longest completed trial on record ran for <strong>{formatMonths(info.longestMonths)}</strong>.
            {info.yearsOrMoreCount > 0
              ? ` ${info.yearsOrMoreCount} completed trial${info.yearsOrMoreCount > 1 ? 's' : ''} ran a year or longer.`
              : ' No completed trial on record ran a full year — most data here reflects shorter-term use.'}
          </p>
          {info.phase4Count > 0 ? (
            <p>
              {info.phase4Count} Phase 4 (post-market) trial{info.phase4Count > 1 ? 's' : ''} on record
              {info.phase4LongestMonths ? `, the longest running ${formatMonths(info.phase4LongestMonths)}` : ''} — these track the drug in real-world use after approval, which is where most long-term signal comes from.
            </p>
          ) : (
            <p>No Phase 4 (post-market surveillance) trials found for this drug in ClinicalTrials.gov — real-world, multi-year safety monitoring may be limited or reported elsewhere.</p>
          )}
        </>
      )}
    </div>
  );
}

function topMatch(events, terms) {
  return events.filter(e => matchAny(e.reaction, terms));
}

// Uses actual enrolled-participant counts from posted trial results, not
// eligibility criteria. Only completed trials that have formally reported
// results carry this data — often a small subset of all trials on record,
// since results reporting can lag completion by months or years.
function getSexRepresentation(trials) {
  const withData = trials.filter(t => t.sexEnrollment);
  if (withData.length === 0) return null;

  const totalFemale = withData.reduce((sum, t) => sum + t.sexEnrollment.female, 0);
  const totalMale = withData.reduce((sum, t) => sum + t.sexEnrollment.male, 0);
  const total = totalFemale + totalMale;
  if (total === 0) return null;

  return {
    trialsWithData: withData.length,
    totalTrials: trials.length,
    totalFemale,
    totalMale,
    total,
    pctFemale: Math.round((totalFemale / total) * 100),
    pctMale: Math.round((totalMale / total) * 100),
  };
}

function SexRepresentation({ trials }) {
  const info = getSexRepresentation(trials);

  if (!info) {
    return (
      <div id="sex-data" className="result-section sexrep-section">
        <h3>⚖ Tested in Men and Women?</h3>
        <p className="section-note">
          None of the trials on record for this drug have posted enrollment data broken down by sex yet. That data is only available once a completed trial formally reports results — which can take months to years after completion — so this doesn't mean the split is unknown forever, just not published here yet.
        </p>
      </div>
    );
  }

  const { trialsWithData, totalTrials, totalFemale, totalMale, total, pctFemale, pctMale } = info;
  const imbalanced = pctFemale <= 35 || pctMale <= 35;

  return (
    <div id="sex-data" className={`result-section sexrep-section ${imbalanced ? 'sexrep-imbalanced' : ''}`}>
      <h3>⚖ Tested in Men and Women?</h3>
      <p className="section-note">
        Based on actual enrolled-participant counts from {trialsWithData} of {totalTrials} trials that have posted results — the rest haven't reported demographic breakdowns yet.
      </p>

      <div className="sexrep-stats">
        <div className="sexrep-stat">
          <span className="sexrep-stat-number">{pctFemale}%</span>
          <span className="sexrep-stat-label">Women ({totalFemale.toLocaleString()})</span>
        </div>
        <div className="sexrep-stat">
          <span className="sexrep-stat-number">{pctMale}%</span>
          <span className="sexrep-stat-label">Men ({totalMale.toLocaleString()})</span>
        </div>
      </div>

      <div className="sexrep-bar-bg">
        <div className="sexrep-bar-seg sexrep-bar-female" style={{ width: `${pctFemale}%` }} />
        <div className="sexrep-bar-seg sexrep-bar-male" style={{ width: `${pctMale}%` }} />
      </div>

      {imbalanced && (
        <p className="sexrep-summary">
          Across {total.toLocaleString()} reported participants, enrollment was skewed: {pctFemale}% women, {pctMale}% men. This may reflect the condition treated, not necessarily a gap in testing.
        </p>
      )}
    </div>
  );
}

const SERIOUS_TERMS = [
  'death', 'fatal', 'mortalit', 'anaphylax', 'stevens-johnson', 'toxic epidermal',
  'myocardial infarction', 'cardiac arrest', 'stroke', 'pulmonary embolism',
  'thrombosis', 'blood clot', 'ventricular fibrillat', 'hepatic failure',
  'liver failure', 'renal failure', 'acute kidney', 'rhabdomyolysis',
  'suicid', 'self-harm', 'sepsis', 'angioedema',
];

const MODERATE_TERMS = [
  'depress', 'anxiety', 'psychos', 'hallucin', 'panic',
  'depend', 'withdrawal', 'abuse', 'addiction',
  'hepat', 'jaundice', 'transaminase',
  'atrial fibrillat', 'chest pain', 'syncope', 'dyspnoea', 'dyspnea',
  'renal impair', 'kidney',
];

// FAERS status/exposure terms — these mean "a pregnancy was reported during
// treatment," not "the drug caused this symptom." Handled as their own
// category so they don't read as physical side effects like nausea.
const PREGNANCY_STATUS_TERMS = [
  'pregnan', 'fetal', 'abortion', 'miscarriage', 'maternal exposure',
];

const MILD_TERMS = [
  'nausea', 'vomit', 'diarrhoea', 'diarrhea', 'constipat',
  'headache', 'dizziness', 'fatigue', 'malaise', 'asthenia',
  'rash', 'pruritus', 'erythema', 'urticaria', 'skin',
  'application site', 'injection site', 'infusion site',
  'hair loss', 'alopecia', 'insomnia', 'somnolence',
  'dry mouth', 'decreased appetite', 'weight',
];

const NOISE_TERMS = [
  'off label', 'drug ineffective', 'adverse drug reaction',
  'product quality', 'wrong drug', 'accidental exposure',
  'no adverse event', 'therapeutic response',
];

function getSideEffectSummary(adverseEvents) {
  if (!adverseEvents || adverseEvents.length === 0) return null;

  const top = adverseEvents.slice(0, 10);

  const serious = top.filter(e => matchAny(e.reaction, SERIOUS_TERMS));
  const pregnancyStatus = top.filter(e =>
    !matchAny(e.reaction, SERIOUS_TERMS) && matchAny(e.reaction, PREGNANCY_STATUS_TERMS)
  );
  const moderate = top.filter(e =>
    !matchAny(e.reaction, SERIOUS_TERMS) &&
    !matchAny(e.reaction, PREGNANCY_STATUS_TERMS) &&
    matchAny(e.reaction, MODERATE_TERMS)
  );
  const mild = top.filter(e =>
    !matchAny(e.reaction, SERIOUS_TERMS) &&
    !matchAny(e.reaction, PREGNANCY_STATUS_TERMS) &&
    !matchAny(e.reaction, MODERATE_TERMS) &&
    matchAny(e.reaction, MILD_TERMS)
  );
  const uncategorized = top.filter(e =>
    !matchAny(e.reaction, SERIOUS_TERMS) &&
    !matchAny(e.reaction, PREGNANCY_STATUS_TERMS) &&
    !matchAny(e.reaction, MODERATE_TERMS) &&
    !matchAny(e.reaction, MILD_TERMS) &&
    !matchAny(e.reaction, NOISE_TERMS)
  );

  let verdict, verdictColor, verdictSummary;
  if (serious.length > 0) {
    verdict = 'Serious reactions in the reports';
    verdictColor = '#c62828';
    verdictSummary = `${serious.length} serious adverse event${serious.length > 1 ? 's' : ''} appear${serious.length === 1 ? 's' : ''} among the top reported reactions. Reports don't show how common these are or whether the drug caused them.`;
  } else if (pregnancyStatus.length > 0) {
    verdict = 'Pregnancy-related reports present';
    verdictColor = '#e65100';
    verdictSummary = `Pregnancy-related terms appear among the top reports — see the note below on what that means before reading it as a symptom.`;
  } else if (moderate.length > 0) {
    verdict = 'Moderate reactions in the reports';
    verdictColor = '#e65100';
    verdictSummary = `No life-threatening events among the top reported reactions. Reports alone can't show whether the drug caused them.`;
  } else if (mild.length > 0 || uncategorized.length > 0) {
    verdict = 'Mostly mild reactions reported';
    verdictColor = '#2e7d32';
    verdictSummary = `The most-reported reactions for this drug are ones generally considered mild. Individual experiences vary.`;
  } else {
    verdict = 'Mostly informational reports';
    verdictColor = '#555';
    verdictSummary = `Top FAERS entries are mostly informational (off-label use, ineffectiveness) rather than specific side effects.`;
  }

  return {
    verdict,
    verdictColor,
    verdictSummary,
    groups: [
      serious.length > 0 && { label: 'Serious reports', color: '#c62828', bg: '#fff5f5', events: serious },
      pregnancyStatus.length > 0 && {
        label: 'Pregnancy-related reports',
        color: '#e65100',
        bg: '#fff8f0',
        events: pregnancyStatus,
        note: 'These aren’t symptoms the drug causes — "pregnancy" as a FAERS term usually means a pregnancy was reported while someone was taking a drug that warns against use during pregnancy, which triggers a safety report on its own.',
      },
      moderate.length > 0 && { label: 'Moderate reports', color: '#e65100', bg: '#fff8f0', events: moderate },
      mild.length > 0 && { label: 'Common reports', color: '#2e7d32', bg: '#f5fff5', events: mild },
      uncategorized.length > 0 && { label: 'Other reports', color: '#555', bg: '#f7f7f7', events: uncategorized },
    ].filter(Boolean),
  };
}

function getKeyFindings(data) {
  const { adverseEvents, recalls, trials, evidenceTier } = data;
  const danger = [];
  const warnings = [];
  const positives = [];

  // ── Recalls ────────────────────────────────────────────
  const ongoingRecalls = recalls.filter(r => r.status === 'Ongoing');
  if (ongoingRecalls.length > 0) {
    danger.push({
      type: 'danger',
      icon: '🚨',
      text: `${ongoingRecalls.length} active recall${ongoingRecalls.length > 1 ? 's' : ''} currently in effect. This product or its class is under recall right now.`
    });
  } else if (recalls.length > 0) {
    danger.push({
      type: 'danger',
      icon: '🔴',
      text: `${recalls.length} past recall${recalls.length > 1 ? 's' : ''} on record. All are resolved, but indicates prior manufacturing or safety issues.`
    });
  }

  // ── Serious FAERS signals (top of the danger list) ─────
  const deathFlags = topMatch(adverseEvents, ['death', 'fatal', 'mortality']);
  if (deathFlags.length > 0) {
    danger.push({
      type: 'danger',
      icon: '⚠️',
      text: `Death is among the reported outcomes in FDA adverse event data (${deathFlags[0].count.toLocaleString()} reports). This does not establish causation.`
    });
  }

  const seriousFlags = topMatch(adverseEvents, ['anaphylax', 'stevens-johnson', 'toxic epidermal', 'angioedema']);
  if (seriousFlags.length > 0) {
    danger.push({
      type: 'danger',
      icon: '⚠️',
      text: `Serious allergic reactions reported: ${seriousFlags.map(e => e.reaction.toLowerCase()).join(', ')} (${seriousFlags[0].count.toLocaleString()} reports).`
    });
  }

  const cardioFlags = topMatch(adverseEvents, ['myocardial infarction', 'cardiac arrest', 'stroke', 'pulmonary embolism', 'ventricular', 'atrial fibrillation', 'thrombosis', 'blood clot']);
  if (cardioFlags.length > 0) {
    danger.push({
      type: 'danger',
      icon: '❤️',
      text: `Cardiovascular events in reported data: ${cardioFlags.slice(0, 2).map(e => e.reaction.toLowerCase()).join(', ')} (${cardioFlags[0].count.toLocaleString()} reports).`
    });
  }

  const liverFlags = topMatch(adverseEvents, ['hepat', 'liver', 'jaundice', 'hepatotox', 'alanine aminotransferase', 'transaminase']);
  if (liverFlags.length > 0) {
    danger.push({
      type: 'danger',
      icon: '⚠️',
      text: `Liver-related events reported: ${liverFlags.slice(0, 2).map(e => e.reaction.toLowerCase()).join(', ')} (${liverFlags[0].count.toLocaleString()} reports).`
    });
  }

  // "Pregnancy" / "exposure during pregnancy" are FAERS status flags, not
  // symptoms — they mean a pregnancy occurred during treatment, which is
  // reportable on its own when a drug warns against use in pregnancy.
  // Birth defects, miscarriage, and induced abortion are real outcomes and
  // stay framed as safety signals.
  const pregnancyStatusFlags = topMatch(adverseEvents, ['pregnan', 'maternal exposure']).filter(e =>
    !matchAny(e.reaction, ['birth defect', 'congenital', 'teratogen', 'miscarriage', 'abortion'])
  );
  const pregnancyOutcomeFlags = topMatch(adverseEvents, ['birth defect', 'congenital', 'teratogen', 'miscarriage', 'abortion']);

  if (pregnancyOutcomeFlags.length > 0) {
    danger.push({
      type: 'danger',
      icon: '🤰',
      text: `Pregnancy-related outcomes reported: ${pregnancyOutcomeFlags.slice(0, 2).map(e => e.reaction.toLowerCase()).join(', ')} (${pregnancyOutcomeFlags[0].count.toLocaleString()} reports).`
    });
  } else if (pregnancyStatusFlags.length > 0) {
    danger.push({
      type: 'danger',
      icon: '🤰',
      text: `"${pregnancyStatusFlags[0].reaction.toLowerCase()}" appears among the top reports (${pregnancyStatusFlags[0].count.toLocaleString()} times) — not a symptom, but a pregnancy occurring during treatment, which is reportable on its own for drugs that warn against use in pregnancy.`
    });
  }

  const suicideFlags = topMatch(adverseEvents, ['suicid', 'self-harm', 'self harm']);
  if (suicideFlags.length > 0) {
    danger.push({
      type: 'danger',
      icon: '⚠️',
      text: `Suicidal ideation or self-harm is among reported adverse events (${suicideFlags[0].count.toLocaleString()} reports). Reports alone don't establish causation.`
    });
  }

  const renalFlags = topMatch(adverseEvents, ['renal failure', 'renal impairment', 'kidney', 'nephropathy', 'acute kidney']);
  if (renalFlags.length > 0) {
    warnings.push({
      type: 'warning',
      icon: '⚠️',
      text: `Kidney-related events reported: ${renalFlags.slice(0, 2).map(e => e.reaction.toLowerCase()).join(', ')} (${renalFlags[0].count.toLocaleString()} reports).`
    });
  }

  const mentalFlags = topMatch(adverseEvents, ['depress', 'anxiety', 'psychos', 'hallucin', 'mania', 'bipolar', 'panic']);
  if (mentalFlags.length > 0) {
    warnings.push({
      type: 'warning',
      icon: '🧠',
      text: `Mental health events reported: ${mentalFlags.slice(0, 2).map(e => e.reaction.toLowerCase()).join(', ')} (${mentalFlags[0].count.toLocaleString()} reports).`
    });
  }

  const dependenceFlags = topMatch(adverseEvents, ['depend', 'withdrawal', 'abuse', 'addiction', 'tolerance']);
  if (dependenceFlags.length > 0) {
    warnings.push({
      type: 'warning',
      icon: '🔒',
      text: `Dependence or withdrawal-related events appear in FAERS data (${dependenceFlags[0].count.toLocaleString()} reports).`
    });
  }

  // Full detail (report count, framing) lives in "Approved Use vs Actual
  // Use" below — keep this pointer brief so it isn't restated twice.
  const offLabel = adverseEvents.find(e => matchAny(e.reaction, ['off label', 'off-label']));
  if (offLabel) {
    warnings.push({
      type: 'warning',
      icon: '📋',
      text: `This drug is commonly used off-label — see "Approved Use vs. Actual Use" below for details.`
    });
  }

  // ── Side effect frequency signal ──────────────────────
  // Exclude pregnancy status flags too — "pregnancy" isn't a side effect,
  // so it shouldn't be quoted as one here either.
  const topReported = adverseEvents
    .filter(e => !matchAny(e.reaction, NOISE_TERMS) && !matchAny(e.reaction, PREGNANCY_STATUS_TERMS))
    .slice(0, 3);

  if (topReported.length > 0) {
    const topCount = topReported[0].count;
    const isHighVolume = topCount >= 5000;
    const names = topReported.map(e => e.reaction.toLowerCase()).join(', ');

    if (isHighVolume) {
      warnings.push({
        type: 'warning',
        icon: '📊',
        text: `Commonly reported: ${names}. The top signal has ${topCount.toLocaleString()} FAERS reports — among the more frequently flagged reactions for this drug.`,
      });
    } else {
      positives.push({
        type: 'safe',
        icon: '📊',
        text: `Reported side effects appear relatively rare in FAERS: top signal is ${topReported[0].reaction.toLowerCase()} (${topCount.toLocaleString()} reports).`,
      });
    }
  }

  // ── Evidence quality ───────────────────────────────────
  const maxEnrollment = Math.max(...trials.map(t => t.enrollment), 0);
  const phase3count = trials.filter(t => t.phase === 'PHASE3' || t.phase === 'PHASE4').length;

  if (evidenceTier.label === 'No FDA Data') {
    warnings.push({
      type: 'warning',
      icon: '🔬',
      text: `No FDA label or clinical trial data found. This may be a supplement, compounded drug, or unapproved product — safety profile is unknown.`
    });
  } else if (evidenceTier.label === 'Thin Evidence') {
    warnings.push({
      type: 'warning',
      icon: '🔬',
      text: `Limited clinical evidence: ${trials.length} trial${trials.length !== 1 ? 's' : ''} found${maxEnrollment > 0 ? `, largest with ${maxEnrollment.toLocaleString()} participants` : ''}. Long-term safety is not well established.`
    });
  } else if (maxEnrollment > 0 && maxEnrollment < 200 && evidenceTier.label !== 'Well-Studied') {
    warnings.push({
      type: 'warning',
      icon: '🔬',
      text: `Largest trial enrolled only ${maxEnrollment} participants — how this drug behaves at scale may not be fully known.`
    });
  }

  // ── Positive signals ───────────────────────────────────
  if (evidenceTier.label === 'Well-Studied') {
    positives.push({
      type: 'safe',
      icon: '✅',
      text: `Strong evidence base: ${phase3count} Phase 3/4 trial${phase3count !== 1 ? 's' : ''} found${maxEnrollment > 0 ? `, with up to ${maxEnrollment.toLocaleString()} participants in a single trial` : ''}. Long-term data exists.`
    });
  } else if (evidenceTier.label === 'Moderate Evidence' && phase3count >= 1) {
    positives.push({
      type: 'safe',
      icon: '✅',
      text: `Reasonable evidence: ${phase3count} Phase 3/4 trial${phase3count !== 1 ? 's' : ''} on record. Some gaps in long-term data may remain.`
    });
  }

  if (recalls.length === 0 && adverseEvents.length > 0) {
    positives.push({
      type: 'safe',
      icon: '✅',
      text: `No recalls on record.`
    });
  }

  // ── Assemble: danger first, then warnings, then positives ──
  const all = [...danger, ...warnings, ...positives];

  if (all.length === 0) {
    if (adverseEvents.length > 0) {
      all.push({ type: 'safe', icon: '✅', text: `No major red flags in FDA adverse event data. Always consult a healthcare provider before use.` });
    } else {
      all.push({ type: 'warning', icon: '📭', text: `No adverse event reports found in FAERS. Limited post-market data — absence of reports is not confirmation of safety.` });
    }
  }

  return all;
}

function formatDate(raw) {
  if (!raw || raw.length !== 8) return raw;
  const year = raw.slice(0, 4);
  const month = raw.slice(4, 6);
  const day = raw.slice(6, 8);
  const date = new Date(`${year}-${month}-${day}`);
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function stripLabelNoise(text) {
  if (!text) return '';
  return text
    .replace(/\d+(\.\d+)*\s+[A-Z][A-Z\s/&-]{3,}(?![a-z])/g, ' ') // "4 CONTRAINDICATIONS", "1 INDICATIONS & USAGE" — without eating the first letter of the next word
    .replace(/^(?:[A-Z]{2,}[\s:,]+){2,}/gm, '')               // leading ALL-CAPS blocks like "WARNINGS PSYCHIATRIC DISORDERS"
    .replace(/^\s*(?:(?:[A-Z]{2,}|&)\s+)+/, '')                // leading ALL-CAPS words incl. "INDICATIONS & USAGE"
    .replace(/\bWARNING[S]?\b:?\s*/g, '')                      // standalone "WARNING:" prefix
    .replace(/\[see [^\]]+\]/gi, '')                            // [see Warnings (5.1)]
    .replace(/\(\s*\d+\.\d+(\.\d+)?\s*\)/g, '')               // (5.1)
    .replace(/\bsee\s+(?:PRECAUTIONS|WARNINGS|ADVERSE)[^.)]+[.)]/gi, '')
    .replace(/\bSee Boxed [A-Z\s]+[.\s]/g, ' ')               // "See Boxed CONTRAINDICATIONS AND ."
    .replace(/\(\s*[.,;:]?\s*\)/g, '')                          // empty parens left by removals
    .replace(/\(\s*[.,]/g, '.')                                  // "(." fragments
    .replace(/\s+\./g, '.')                                     // orphaned " ." left by removals
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// FDA labels frequently repeat the boxed warning verbatim as the opening of
// the general "Warnings" section. If the two overlap heavily at the start,
// drop the overlapping lead-in from `warningsText` so it isn't shown twice
// on the page — the full text already has its own card above this one.
function stripBoxedWarningOverlap(warningsText, boxedWarningText) {
  if (!warningsText || !boxedWarningText) return warningsText;
  const cleanWarnings = stripLabelNoise(warningsText);
  const cleanBoxed = stripLabelNoise(boxedWarningText);
  const sample = cleanBoxed.slice(0, 120).toLowerCase();
  if (sample.length < 40) return warningsText;

  const idx = cleanWarnings.toLowerCase().indexOf(sample);
  // Only treat this as "the warnings section restates the boxed warning as
  // its opening" if the match is right at the start. A match deep in a long
  // warnings section (labels often cover pregnancy both in the boxed
  // warning AND again in their own Pregnancy subsection) is a separate,
  // legitimate topic — stripping everything before it would delete real
  // safety content like "Psychiatric Disorders" that has nothing to do with
  // the boxed warning.
  if (idx === -1 || idx > 40) return warningsText;

  // Walk forward from the overlap to the next sentence boundary so we don't
  // cut mid-sentence, then drop everything before that point. If the
  // overlap already ends right on punctuation, we're already at a
  // boundary — don't walk past it looking for a *different* one.
  let cursor = idx + sample.length;
  if (!/[.!?]/.test(cleanWarnings[cursor - 1] || '')) {
    while (cursor < cleanWarnings.length && !/[.!?]/.test(cleanWarnings[cursor])) cursor++;
    cursor++; // move past the punctuation itself
  }
  const remainder = cleanWarnings.slice(cursor).trim();
  return remainder.length > 20 ? remainder : warningsText;
}

function deduplicateBullets(bullets) {
  const seen = [];
  return bullets.filter(b => {
    const key = b.slice(0, 50).toLowerCase();
    if (seen.some(s => key.includes(s) || s.includes(key))) return false;
    seen.push(key);
    return true;
  });
}

function extractBullets(text) {
  if (!text || !text.includes('•')) return null;
  const bullets = text
    .split('•')
    .slice(1)
    .map(b => stripLabelNoise(b).replace(/\s+/g, ' ').trim())
    .filter(b => b.length > 8)
    .map(b => b.replace(/\.$/, ''));
  return deduplicateBullets(bullets);
}

function extractInteractions(text) {
  if (!text) return null;

  // Format 1: numbered subsections — "7.1 Concomitant Use..."
  if (/\d+\.\d+\s+[A-Z]/.test(text)) {
    const parts = text.split(/\d+\.\d+\s+(?=[A-Z])/).slice(1);
    if (parts.length > 0) {
      return parts
        .map(part => {
          const first = part.split(/\.\s+[A-Z]/)[0];
          return stripLabelNoise(first).replace(/\s+/g, ' ').trim();
        })
        .filter(s => s.length > 20)
        .slice(0, 7);
    }
  }

  // Format 2: named sections — "Vitamin A: Because..."
  const namedPattern = /([A-Z][A-Za-z0-9][\w\s/,.'-]{1,50}?)\s*:\s*([^:]{30,}?)(?=\s[A-Z][A-Za-z][\w\s.'-]{2,}:|$)/g;
  const results = [];
  let m;
  const clean = stripLabelNoise(text).replace(/^Drug Interactions:?\s*/i, '');
  while ((m = namedPattern.exec(clean)) !== null) {
    const name = m[1].trim();
    const desc = m[2].split(/\.\s+/)[0].trim();
    if (name.length < 55 && desc.length > 15 && !/^\d/.test(name)) {
      results.push(`${name}: ${desc}`);
    }
  }
  if (results.length > 1) return deduplicateBullets(results).slice(0, 7);

  return null;
}

function extractWarningTopics(text) {
  if (!text) return null;
  // Pull Title Case section headers (≥2 words, all start with capital)
  const pattern = /(?:^|\.\s+|\n)([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,4})(?:\s+[A-Z][a-z])/gm;
  const topics = [];
  let m;
  while ((m = pattern.exec(text)) !== null) {
    const t = m[1].trim();
    if (t.length > 6 && t.length < 45 && !topics.includes(t)) topics.push(t);
  }
  return topics.length >= 2 ? topics.slice(0, 10) : null;
}

// Words too generic to identify a condition on their own
const CONDITION_STOPWORDS = ['disease', 'disorder', 'syndrome', 'chronic', 'acute', 'severe', 'type', 'stage', 'adult', 'pediatric', 'patients', 'mellitus', 'infection'];

function conditionInApprovedUse(condition, purposeText) {
  const purpose = (purposeText || '').toLowerCase();
  const words = condition.toLowerCase()
    .replace(/[(),]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 3 && !CONDITION_STOPWORDS.includes(w));
  if (words.length === 0) return true; // too generic to judge — don't flag it
  return words.some(w => purpose.includes(w));
}

function getHypeCheck(data) {
  const { drugInfo, adverseEvents, conditionMap, name } = data;
  if (!drugInfo?.purpose || conditionMap.length === 0) return null;

  const offLabel = adverseEvents.find(e => matchAny(e.reaction, ['off label', 'off-label']));

  const beyondLabel = conditionMap
    .filter(c => !conditionInApprovedUse(c.condition, drugInfo.purpose))
    .filter(c => !c.condition.toLowerCase().includes(name.toLowerCase()))
    .slice(0, 4);

  // Only show when there's an actual story
  if (!offLabel && beyondLabel.length < 2) return null;

  const hasLatePhase = beyondLabel.some(c => c.phases.includes('PHASE3') || c.phases.includes('PHASE4'));

  let verdictText;
  if (offLabel && offLabel.count >= 1000) {
    verdictText = `This drug is widely used beyond what it's approved for — "off-label use" shows up ${offLabel.count.toLocaleString()} times in FDA adverse event reports. That doesn't make those uses wrong, but the evidence behind them is ${hasLatePhase ? 'mixed: some have late-stage trials, others don\'t' : 'thinner than for the approved use'}.`;
  } else if (offLabel) {
    verdictText = `Some people use this drug beyond its approved purpose. The evidence for those uses is ${hasLatePhase ? 'building — some late-stage trials exist' : 'still early'}.`;
  } else {
    verdictText = `Researchers are testing this drug for conditions beyond what it's approved for. ${hasLatePhase ? 'Some of that research has reached late-stage trials.' : 'That research is still in early stages.'}`;
  }

  return { beyondLabel, offLabel, verdictText };
}

function SaveMedButton({ data }) {
  const [saved, setSaved] = useState(isSaved(data.name));

  const toggle = () => {
    if (saved) {
      removeMed(data.name);
      setSaved(false);
    } else {
      saveMed({
        name: data.name,
        genericName: data.drugInfo?.genericName !== 'Unknown' ? data.drugInfo?.genericName : null,
        tier: data.evidenceTier.label,
        tierColor: data.evidenceTier.color,
      });
      setSaved(true);
    }
  };

  return (
    <button className={`save-med-button ${saved ? 'save-med-button--saved' : ''}`} onClick={toggle}>
      {saved ? '✓ In my meds' : '+ My meds'}
    </button>
  );
}

// What someone starting this drug should know — built only from data on the page
function getStartingGuide(data) {
  const { drugInfo, adverseEvents, recalls } = data;
  if (!drugInfo || adverseEvents.length === 0) return null;

  const top = adverseEvents.slice(0, 10);
  const common = top.filter(e =>
    !matchAny(e.reaction, NOISE_TERMS) &&
    !matchAny(e.reaction, SERIOUS_TERMS) &&
    matchAny(e.reaction, MILD_TERMS)
  ).slice(0, 4);

  const serious = top.filter(e => matchAny(e.reaction, SERIOUS_TERMS)).slice(0, 3);
  const moderate = top.filter(e =>
    !matchAny(e.reaction, SERIOUS_TERMS) && matchAny(e.reaction, MODERATE_TERMS)
  ).slice(0, 3);

  const ongoingRecalls = recalls.filter(r => r.status === 'Ongoing').length;

  // Nothing useful to say → don't show an empty card
  if (common.length === 0 && serious.length === 0 && moderate.length === 0 && !drugInfo.boxedWarning && ongoingRecalls === 0) {
    return null;
  }

  return { common, serious, moderate, hasBoxedWarning: !!drugInfo.boxedWarning, ongoingRecalls };
}

function StartingGuide({ data }) {
  const guide = getStartingGuide(data);
  if (!guide) return null;

  const displayName = data.drugInfo?.brandName || data.name;

  return (
    <div className="result-section starting-guide">
      <h3>💊 Starting {displayName.toLowerCase()}?</h3>
      <p className="section-note">Based on what other people taking this drug have reported to the FDA. Everyone is different — this is what's common, not what will happen to you.</p>

      {guide.common.length > 0 && (
        <div className="sg-block">
          <p className="sg-label sg-label--common">Most frequently reported</p>
          <p className="sg-text">
            {guide.common.map(e => e.reaction.toLowerCase()).join(', ')}. These are the most common reports for this drug. Reports alone can't show whether the drug caused them, and they don't happen to everyone.
          </p>
        </div>
      )}

      {(guide.serious.length > 0 || guide.moderate.length > 0) && (
        <div className="sg-block">
          <p className="sg-label sg-label--serious">Serious reactions also in the reports</p>
          <p className="sg-text">
            {[...guide.serious, ...guide.moderate].map(e => e.reaction.toLowerCase()).join(', ')} also appear in the reported data. Talk to your prescriber or pharmacist about any symptoms that concern you.
          </p>
        </div>
      )}

      {guide.hasBoxedWarning && (
        <div className="sg-block">
          <p className="sg-label sg-label--boxed">Black box warning on file</p>
          <p className="sg-text">
            This drug's FDA label carries a black box warning — the FDA's most serious warning category. The details are in the black-bordered section below.
          </p>
        </div>
      )}

      {guide.ongoingRecalls > 0 && (
        <div className="sg-block">
          <p className="sg-label sg-label--boxed">Active recall on record</p>
          <p className="sg-text">
            There {guide.ongoingRecalls === 1 ? 'is an active recall' : `are ${guide.ongoingRecalls} active recalls`} on record for this drug or its class. Details are in the Recalls section below — a pharmacist can confirm whether a specific batch is affected.
          </p>
        </div>
      )}
    </div>
  );
}

function ShareButton({ drugName }) {
  const [copied, setCopied] = useState(false);

  const handleShare = async () => {
    const url = `${window.location.origin}${window.location.pathname}?drug=${encodeURIComponent(drugName)}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: `${drugName} — Evidently`, url });
        return;
      } catch { /* user cancelled — fall through to copy */ }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  return (
    <button className="share-button" onClick={handleShare}>
      {copied ? '✓ Link copied' : '🔗 Share'}
    </button>
  );
}

function HypeCheck({ data }) {
  const check = getHypeCheck(data);
  if (!check) return null;

  return (
    <div className="result-section hype-check">
      <h3>🔍 Approved Use vs. Actual Use</h3>
      <div className="hype-row">
        <span className="hype-tag hype-tag--approved">FDA-approved for</span>
        <div className="hype-text">
          <ShowMore text={data.drugInfo.purpose} limit={160} />
        </div>
      </div>
      {check.beyondLabel.length > 0 && (
        <div className="hype-row">
          <span className="hype-tag hype-tag--tested">Also being tested for</span>
          <div className="hype-conditions">
            {check.beyondLabel.map((c, i) => (
              <span key={i} className="hype-condition-chip">
                {c.condition} · {c.count} {c.count === 1 ? 'trial' : 'trials'}
              </span>
            ))}
          </div>
        </div>
      )}
      <p className="hype-verdict">{check.verdictText}</p>
    </div>
  );
}

function BulletList({ items }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, 4);
  return (
    <>
      <ul className="label-bullets">
        {visible.map((item, i) => <li key={i}>{item}</li>)}
      </ul>
      {items.length > 4 && (
        <button className="show-more-btn" onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Show less' : `Show ${items.length - 4} more`}
        </button>
      )}
    </>
  );
}

function WarningTopics({ text }) {
  const [expanded, setExpanded] = useState(false);
  const clean = stripLabelNoise(text);
  // Extract from the cleaned text, not raw — the raw text usually starts
  // with an ALL-CAPS "WARNINGS" header directly followed by the first
  // topic with no punctuation between them (e.g. "WARNINGS Psychiatric
  // Disorders..."), which the topic regex can't anchor on. Once
  // stripLabelNoise removes that header, the first real topic sits at the
  // start of the string where the regex can match it.
  const topics = extractWarningTopics(clean);

  if (!topics) return <ShowMore text={text} limit={220} />;

  return (
    <>
      <p className="section-note" style={{ marginBottom: 10 }}>Areas flagged in the FDA label:</p>
      <div className="warning-topics">
        {topics.map((t, i) => <span key={i} className="warning-topic-tag">{t}</span>)}
      </div>
      {expanded && <p style={{ marginTop: 12, fontSize: '0.88rem', lineHeight: 1.6, color: '#333' }}>{clean}</p>}
      <button className="show-more-btn" style={{ marginTop: 10 }} onClick={() => setExpanded(e => !e)}>
        {expanded ? 'Show less' : 'Read full warnings'}
      </button>
    </>
  );
}

function ShowMore({ text, limit = 200 }) {
  const [expanded, setExpanded] = useState(false);
  const clean = stripLabelNoise(text);
  if (!clean) return null;
  const isLong = clean.length > limit;
  return (
    <p>
      {isLong && !expanded ? clean.slice(0, limit).trimEnd() + '…' : clean}
      {isLong && (
        <button className="show-more-btn" onClick={() => setExpanded(e => !e)}>
          {expanded ? ' Show less' : ' Show more'}
        </button>
      )}
    </p>
  );
}

// Lower visual priority than the main findings — collapsed to the top 5
// by default since this list is for the curious, not the urgent
function ConditionsStudied({ conditionMap, maxConditionCount }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? conditionMap : conditionMap.slice(0, 5);

  return (
    <div className="result-section conditions-studied">
      <h3>Conditions Studied</h3>
      <p className="section-note">What this drug has been tested on — may go beyond its approved use.</p>
      <div className="condition-list">
        {visible.map((item, i) => (
          <div key={i} className="condition-item">
            <div className="condition-header">
              <span className="condition-name">{item.condition}</span>
              <span className="condition-count">{item.count} {item.count === 1 ? 'trial' : 'trials'}</span>
            </div>
            <div className="condition-bar-bg">
              <div className="condition-bar-fill" style={{ width: `${Math.round((item.count / maxConditionCount) * 100)}%` }} />
            </div>
            {item.phases.length > 0 && (
              <div className="condition-phases">
                {item.phases.map((p, j) => (
                  <span key={j} className="condition-phase-tag">{phaseLabel(p)}</span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {conditionMap.length > 5 && (
        <button className="show-more-btn" style={{ marginTop: 10 }} onClick={() => setExpanded(e => !e)}>
          {expanded ? 'Show less' : `Show ${conditionMap.length - 5} more`}
        </button>
      )}
    </div>
  );
}

function phaseLabel(phase) {
  const map = {
    PHASE1: 'Phase 1',
    PHASE2: 'Phase 2',
    PHASE3: 'Phase 3',
    PHASE4: 'Phase 4',
    NA: 'N/A',
  };
  return map[phase] || phase;
}

// Built from structured fields only (enrollment, phase, conditions) —
// deliberately not a paraphrase of the free-text trial title, since
// guessing at what an academic title "means" risks stating something the
// trial didn't actually study. The raw title stays one click away.
function cleanTrialConditions(conditions) {
  if (!conditions || conditions.length === 0) return [];
  const seen = new Set();
  const out = [];
  conditions.forEach(c => {
    const key = c.toLowerCase().trim();
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(c.trim());
  });
  return out;
}

function generatePlainTrialSummary(trial) {
  const conditions = cleanTrialConditions(trial.conditions).slice(0, 2);
  const conditionText = conditions.length > 0 ? conditions.map(c => c.toLowerCase()).join(' and ') : null;
  const phaseText = phaseLabel(trial.phase);
  const hasPhase = trial.phase && trial.phase !== 'Unknown' && trial.phase !== 'NA';
  const sizeText = trial.enrollment > 0 ? `${trial.enrollment.toLocaleString()}-person ` : '';

  let sentence = `A ${sizeText}${hasPhase ? phaseText + ' ' : ''}study`.replace(/\s+/g, ' ').trim();
  if (conditionText) sentence += ` on ${conditionText}`;
  return sentence + '.';
}

function TrialTitle({ trial }) {
  const [showOriginal, setShowOriginal] = useState(false);
  return (
    <>
      <p className="trial-title">{showOriginal ? trial.title : generatePlainTrialSummary(trial)}</p>
      <button className="trial-title-toggle" onClick={() => setShowOriginal(s => !s)}>
        {showOriginal ? 'Show plain-English summary' : 'View original title'}
      </button>
    </>
  );
}

// One or two plain-language sentences answering "should I be concerned?" —
// built entirely from data already computed elsewhere on the page (evidence
// tier, boxed warning presence, recall history, side-effect severity), so
// it's a synthesis of what's shown, not a new judgment call or API call.
function getTopLineSynthesis(data) {
  const { evidenceTier, drugInfo, recalls, adverseEvents } = data;
  const hasBoxedWarning = !!drugInfo?.boxedWarning;
  const ongoingRecalls = recalls.filter(r => r.status === 'Ongoing').length;
  const pastRecalls = recalls.length - ongoingRecalls;
  const sideEffectSummary = getSideEffectSummary(adverseEvents);

  const evidencePhrase = {
    'Well-Studied': 'a well-studied drug with a large clinical trial base',
    'Moderate Evidence': 'a moderately studied drug with some evidence gaps',
    'Thin Evidence': 'a thinly studied drug with limited long-term data',
    'No FDA Data': 'a product with no FDA approval or clinical trial data on record',
  }[evidenceTier.label] || 'a drug with an unclear evidence base';

  const evidenceIsFavorable = evidenceTier.label === 'Well-Studied' || evidenceTier.label === 'Moderate Evidence';

  let sideEffectPhrase = null;
  let sideEffectIsMild = false;
  if (sideEffectSummary) {
    if (sideEffectSummary.verdict.includes('Serious')) {
      sideEffectPhrase = 'serious reactions have been reported';
    } else if (sideEffectSummary.verdict.includes('Pregnancy')) {
      sideEffectPhrase = 'pregnancy-related reports appear in the data';
    } else if (sideEffectSummary.verdict.includes('Moderate')) {
      sideEffectPhrase = 'some moderate reactions have been reported';
    } else if (sideEffectSummary.verdict.includes('mild')) {
      sideEffectPhrase = 'reported side effects are mostly mild';
      sideEffectIsMild = true;
    }
  }

  const sentence1 = `This is ${evidencePhrase}${sideEffectPhrase ? `, and ${sideEffectPhrase}` : ''}.`;

  const concerns = [];
  if (hasBoxedWarning) concerns.push('carries a serious FDA black box warning worth reading before starting it');
  if (ongoingRecalls > 0) concerns.push(`is currently under ${ongoingRecalls === 1 ? 'an active recall' : `${ongoingRecalls} active recalls`}`);
  else if (pastRecalls > 0) concerns.push('has past recalls on record (now resolved)');

  let sentence2 = null;
  if (concerns.length > 0) {
    // "but" reads better when the concern contrasts with an otherwise
    // reassuring picture; "also" when it's just adding to already-mixed signals
    const connector = evidenceIsFavorable && sideEffectIsMild ? 'But it' : 'It also';
    sentence2 = `${connector} ${concerns.join(' and ')}.`;
  } else if (!sideEffectPhrase && evidenceIsFavorable) {
    sentence2 = 'No recalls or black box warnings are on record for this drug.';
  }

  return [sentence1, sentence2].filter(Boolean).join(' ');
}

function TopLineSynthesis({ data }) {
  const summary = getTopLineSynthesis(data);
  return (
    <div className="topline-synthesis" style={{ borderColor: data.evidenceTier.color }}>
      <p className="topline-text">{summary}</p>
      <p className="topline-disclaimer">Summarized from the FDA and clinical trial data below — not medical advice.</p>
    </div>
  );
}

function DrugResults({ data, onSearch }) {
  const { name, drugInfo, brandNames, adverseEvents, recalls, trials, conditionMap, evidenceTier } = data;

  const primaryName = drugInfo?.brandName || name;
  const genericName = drugInfo?.genericName &&
    drugInfo.genericName !== 'Unknown' &&
    drugInfo.genericName.toUpperCase() !== primaryName.toUpperCase()
    ? drugInfo.genericName
    : null;
  const findings = getKeyFindings(data);
  const sideEffectSummary = getSideEffectSummary(adverseEvents);
  const maxEnrollment = Math.max(...trials.map(t => t.enrollment), 0);
  const maxConditionCount = conditionMap.length > 0 ? conditionMap[0].count : 1;

  const navItems = [
    { id: 'findings', label: 'Summary' },
    { id: 'sex-data', label: '⚖ Sex Data' },
    adverseEvents.length > 0 && { id: 'side-effects', label: 'Side Effects' },
    (drugInfo?.contraindications || drugInfo?.drugInteractions) && { id: 'interactions', label: 'Interactions' },
    { id: 'recalls', label: `Recalls${recalls.length > 0 ? ` (${recalls.length})` : ''}` },
    { id: 'long-term', label: 'Long-Term Data' },
    { id: 'trials', label: 'Trials' },
    { id: 'compare', label: '⚖ Compare' },
  ].filter(Boolean);

  return (
    <div className="results-container">
      {data.didYouMean && (
        <div className="did-you-mean">
          Did you mean{' '}
          <button className="dym-button" onClick={() => onSearch && onSearch(data.didYouMean)}>
            {data.didYouMean}
          </button>
          ?
        </div>
      )}

      {/* Top-line synthesis — plain-language "should I be concerned?" answer,
          dominant at the very top of the page, above the Evidence Tier card */}
      <TopLineSynthesis data={data} />

      {/* Drug Name + Evidence Tier */}
      <div className="result-header">
        <div className="result-title-row">
          <h2 className="primary-drug-name">{primaryName}</h2>
          <div className="result-title-actions">
            <SaveMedButton key={name} data={data} />
            <ShareButton drugName={name} />
          </div>
        </div>
        {genericName && <p className="generic-name-prominent">{genericName}</p>}
        {brandNames && brandNames.length > 0 && (
          <div className="brand-names-row">
            <span className="brand-names-label">Also sold as</span>
            {brandNames.filter(b => b.toUpperCase() !== primaryName.toUpperCase()).map((b, i) => (
              <button key={i} className="brand-name-tag" onClick={() => onSearch(b)}>{b}</button>
            ))}
          </div>
        )}
        {drugInfo?.manufacturer && <p className="manufacturer-name">{drugInfo.manufacturer}</p>}

        <div className="evidence-tier" style={{ borderColor: evidenceTier.color }}>
          <span className="tier-label" style={{ color: evidenceTier.color }}>{evidenceTier.label}</span>
          <p className="tier-description">{evidenceTier.description}</p>
          <div className="tier-numbers">
            <div className="tier-number-item">
              <span className="tier-number">{trials.length}</span>
              <span className="tier-number-label">Total Trials</span>
            </div>
            <div className="tier-number-item">
              <span className="tier-number">{maxEnrollment.toLocaleString()}</span>
              <span className="tier-number-label">Largest Enrollment</span>
            </div>
            <div className="tier-number-item">
              <span className="tier-number">{trials.filter(t => t.phase === 'PHASE3' || t.phase === 'PHASE4').length}</span>
              <span className="tier-number-label">Phase 3/4 Trials</span>
            </div>
            <div className="tier-number-item">
              <span className="tier-number">{recalls.length}</span>
              <span className="tier-number-label">Recalls on Record</span>
            </div>
          </div>
          {evidenceTier.label === 'No FDA Data' && (
            <div className="no-data-warning">
              ⚠️ Absence of FDA data does not mean this product is safe. It may be a supplement, compounded substance, or unapproved product with unknown long-term effects.
            </div>
          )}
          {evidenceTier.label === 'Thin Evidence' && !trials.length && (
            <div className="no-data-warning">
              ℹ️ FDA-approved but not found in ClinicalTrials.gov. Trial data may exist under a different name or through non-public registries.
            </div>
          )}
          <p className="tier-sample-note">Based on up to 50 trials from ClinicalTrials.gov. Actual count may be higher. Older drugs may score lower because studies from before the registry existed (2000) aren't counted.</p>
        </div>
      </div>

      {/* Section nav */}
      <nav className="section-nav">
        {navItems.map(item => (
          <a key={item.id} href={`#${item.id}`} className="section-nav-pill">{item.label}</a>
        ))}
      </nav>

      {/* Key Findings */}
      <div id="findings" className="result-section key-findings">
        <h3>Key Findings</h3>
        <div className="findings-list">
          {findings.map((f, i) => (
            <div key={i} className={`finding-item finding-${f.type}`}>
              <span className="finding-icon">{f.icon}</span>
              <span className="finding-text">{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Sex Representation — surfaced high since it's easy to miss otherwise */}
      {trials.length > 0 && <SexRepresentation trials={trials} />}

      {/* Starting this drug? — the just-prescribed lens */}
      <StartingGuide data={data} />

      {/* Black Box Warning — the FDA's most serious flag */}
      {drugInfo?.boxedWarning && (() => {
        const bullets = extractBullets(drugInfo.boxedWarning);
        return (
          <div className="result-section boxed-warning">
            <h3>⬛ Black Box Warning</h3>
            <p className="boxed-warning-note">The FDA's most serious warning — required when there is evidence of a serious or life-threatening risk.</p>
            {bullets ? <BulletList items={bullets} /> : <ShowMore text={drugInfo.boxedWarning} limit={500} />}
          </div>
        );
      })()}

      {/* Hype Check: approved use vs actual use */}
      <HypeCheck data={data} />

      {/* Side Effects */}
      <div id="side-effects" className="result-section">
        <h3>Reported Side Effects</h3>
        <p className="section-note">Source: FDA Adverse Event Reporting System (FAERS) · These are raw, unverified reports — not rates, and not proof the drug caused the reaction. Widely used drugs always have more reports.</p>

        {sideEffectSummary && (
          <div className="sfe-card">
            <div className="sfe-verdict" style={{ borderLeftColor: sideEffectSummary.verdictColor }}>
              <span className="sfe-verdict-label" style={{ color: sideEffectSummary.verdictColor }}>
                {sideEffectSummary.verdict}
              </span>
              <p className="sfe-verdict-text">{sideEffectSummary.verdictSummary}</p>
            </div>
            {sideEffectSummary.groups.length > 0 && (
              <div className="sfe-groups">
                {sideEffectSummary.groups.map((group, gi) => (
                  <div key={gi} className="sfe-group" style={{ backgroundColor: group.bg, borderColor: group.color + '33' }}>
                    <p className="sfe-group-label" style={{ color: group.color }}>{group.label}</p>
                    <div className="sfe-group-events">
                      {group.events.map((e, ei) => (
                        <span key={ei} className="sfe-event-tag" style={{ color: group.color, backgroundColor: group.bg, borderColor: group.color + '55' }}>
                          {e.reaction.toLowerCase()}
                        </span>
                      ))}
                    </div>
                    {group.note && <p className="sfe-group-note">{group.note}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {adverseEvents.length === 0 ? (
          <p>No adverse event reports found.</p>
        ) : (
          <div className="adverse-list">
            {adverseEvents.map((event, i) => {
              const max = adverseEvents[0].count;
              const pct = Math.round((event.count / max) * 100);
              return (
                <div key={i} className="adverse-item">
                  <div className="adverse-header">
                    <span className="adverse-name">{event.reaction}</span>
                    <span className="adverse-count">{event.count.toLocaleString()} reports</span>
                  </div>
                  <div className="adverse-bar-bg">
                    <div
                      className="adverse-bar-fill"
                      style={{ width: `${pct}%`, opacity: Math.max(0.3, 1 - (i * 0.12)) }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Contraindications + Interactions */}
      {(drugInfo?.contraindications || drugInfo?.drugInteractions) && (
        <div id="interactions" className="result-section">
          {drugInfo?.contraindications && (() => {
            const bullets = extractBullets(drugInfo.contraindications);
            return (
              <>
                <h3>Who Should Not Take This</h3>
                <p className="section-note">From the FDA label — conditions where this drug should not be used.</p>
                {bullets ? <BulletList items={bullets} /> : <ShowMore text={drugInfo.contraindications} />}
              </>
            );
          })()}
          {drugInfo?.drugInteractions && (() => {
            const interactions = extractInteractions(drugInfo.drugInteractions);
            return (
              <>
                <h3 style={{ marginTop: drugInfo?.contraindications ? '20px' : 0 }}>Drug Interactions</h3>
                <p className="section-note">Tell your provider about all medications you take.</p>
                {interactions
                  ? <BulletList items={interactions} />
                  : <ShowMore text={drugInfo.drugInteractions} />}
              </>
            );
          })()}
          {drugInfo?.warnings && (
            <>
              <h3 style={{ marginTop: '20px' }}>FDA Warnings</h3>
              <WarningTopics text={stripBoxedWarningOverlap(drugInfo.warnings, drugInfo.boxedWarning)} />
            </>
          )}
        </div>
      )}

      {/* If no contraindications/interactions section, still show warnings */}
      {!drugInfo?.contraindications && !drugInfo?.drugInteractions && drugInfo?.warnings && (
        <div className="result-section warnings">
          <h3>FDA Warnings</h3>
          <WarningTopics text={stripBoxedWarningOverlap(drugInfo.warnings, drugInfo.boxedWarning)} />
        </div>
      )}

      {/* Recalls */}
      <div id="recalls" className="result-section">
        <h3>Recall History</h3>
        {recalls.length === 0 ? (
          <p className="no-recalls">✅ No recalls found for this drug.</p>
        ) : (
          <div className="recalls-list">
            {recalls.map((recall, i) => (
              <div key={i} className={`recall-card ${recall.status === 'Ongoing' ? 'recall-ongoing' : ''}`}>
                {recall.status === 'Ongoing' && <span className="recall-badge">🚨 ACTIVE RECALL</span>}
                <p><strong>Reason:</strong> {recall.reason}</p>
                <p><strong>Date:</strong> {formatDate(recall.date)}</p>
                <p><strong>Status:</strong> {recall.status}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Long-Term Safety */}
      {trials.length > 0 && <LongTermSafety trials={trials} />}

      {/* Clinical Trials */}
      <div id="trials" className="result-section">
        <h3>Clinical Trials</h3>
        {trials.length === 0 ? (
          <p>No clinical trials found in the ClinicalTrials.gov database.</p>
        ) : (
          <>
            <p className="section-note">Showing the strongest evidence first — latest phase, then largest enrollment.</p>
            <div className="trials-list">
              {[...trials]
                .sort((a, b) => {
                  const order = { PHASE4: 4, PHASE3: 3, PHASE2: 2, PHASE1: 1 };
                  const pd = (order[b.phase] || 0) - (order[a.phase] || 0);
                  if (pd !== 0) return pd;
                  return b.enrollment - a.enrollment;
                })
                .slice(0, 5)
                .map((trial, i) => (
                  <div key={i} className="trial-card">
                    <TrialTitle trial={trial} />
                    <div className="trial-meta">
                      <span className="trial-phase">{phaseLabel(trial.phase)}</span>
                      <span>{trial.enrollment.toLocaleString()} participants</span>
                      <span>{trial.status}</span>
                    </div>
                  </div>
                ))}
            </div>
          </>
        )}
      </div>

      {/* Conditions studied — for the curious; lower priority, collapsed by default */}
      {conditionMap.length > 0 && <ConditionsStudied conditionMap={conditionMap} maxConditionCount={maxConditionCount} />}

      {/* FDA Approval — least urgent, at the bottom */}
      {drugInfo && (
        <div className="result-section">
          <h3>FDA Approval</h3>
          <p><strong>Approved for:</strong></p>
          <ShowMore text={drugInfo.purpose} limit={180} />
          <p><strong>Application number:</strong> {drugInfo.approvalDate}</p>
        </div>
      )}
    </div>
  );
}

export default DrugResults;